import React, { useState, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { parseFile, validateCSVData, cleanCSVData } from '../../../utils/csvParser';
import { useToastContext } from '../../../context/ToastContext';
import FieldMappingModal from './FieldMappingModal';

const ImportModal = ({ isOpen, onClose, selectedEvent, onImportComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Field Mapping, 3: Preview & Import
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const toast = useToastContext();

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      console.log('📥 Import Modal opened');
      setStep(1);
      setFile(null);
      setParsedData(null);
      setUploadError(null);
    }
  }, [isOpen]);

  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = ['.csv', '.txt', '.xlsx', '.xls', '.ods'];
    const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      setUploadError('Please select a CSV, Excel (.xlsx, .xls), or ODS file');
      return;
    }

    // Validate file size (10MB limit)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setUploadError(null);
    setIsUploading(true);

    try {
      console.log('📁 Parsing file:', selectedFile.name);
      const parsed = await parseFile(selectedFile);
      console.log('📊 Parsed data:', parsed);

      // Validate parsed data
      const validation = validateCSVData(parsed);
      if (!validation.isValid) {
        setUploadError(`Invalid file: ${validation.errors.join(', ')}`);
        return;
      }

      // Clean the data
      const cleaned = cleanCSVData(parsed);
      setParsedData(cleaned);

      // Show warnings if any
      if (validation.warnings.length > 0) {
        toast.warning(`File loaded with warnings: ${validation.warnings.join(', ')}`);
      } else {
        toast.success(`File loaded successfully! Found ${cleaned.totalRows} rows with ${cleaned.headers.length} columns.`);
      }

      // Move to field mapping step
      setStep(2);
    } catch (error) {
      console.error('File parsing error:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  }, [toast]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleMappingComplete = (result) => {
    setShowMappingModal(false);
    
    if (result.success) {
      // Import completed successfully
      onImportComplete(result);
    } else {
      // Import failed, stay on current step
      toast.error(result.message || 'Import failed');
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setParsedData(null);
    setUploadError(null);
    setShowMappingModal(false);
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <Modal.Header>
          <h2 className="text-xl font-semibold text-gray-900">Import Attendees</h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload a CSV or Excel file to import attendees to your event
          </p>
        </Modal.Header>

        <Modal.Content>
          {/* Step Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${step >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 1 ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">Upload File</span>
              </div>
              <div className={`flex-1 h-px mx-4 ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
              <div className={`flex items-center ${step >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 2 ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Map Fields</span>
              </div>
            </div>
          </div>

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  uploadError 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                {isUploading ? (
                  <div className="space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                    <p className="text-sm text-gray-600">Processing file...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-gray-900">
                        Drop your file here, or{' '}
                        <label className="text-primary-600 hover:text-primary-700 cursor-pointer underline">
                          browse
                          <input
                            type="file"
                            accept=".csv,.txt,.xlsx,.xls,.ods"
                            onChange={handleFileInputChange}
                            className="hidden"
                          />
                        </label>
                      </p>
                      <p className="text-sm text-gray-500">
                        Supports CSV, Excel (.xlsx, .xls), and ODS files (max 10MB)
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Error Display */}
              {uploadError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{uploadError}</p>
                </div>
              )}

              {/* File Info */}
              {file && !uploadError && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">File Requirements:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• First row should contain column headers</li>
                  <li>• Include columns for required fields (Name, Email)</li>
                  <li>• Use clear column names (e.g., "Full Name", "Email Address", "Phone Number")</li>
                  <li>• Remove any empty rows or columns</li>
                  <li>• Excel files: Use the first worksheet only</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Field Mapping Preview */}
          {step === 2 && parsedData && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h4 className="text-sm font-medium text-green-900">File Loaded Successfully</h4>
                </div>
                <div className="text-sm text-green-800">
                  <p>Found {parsedData.totalRows} rows with {parsedData.headers.length} columns</p>
                  <p className="mt-1">Columns: {parsedData.headers.join(', ')}</p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Next, we'll help you map your CSV columns to the event's custom fields.
                </p>
                <Button
                  onClick={() => setShowMappingModal(true)}
                  className="gap-2"
                >
                  Continue to Field Mapping
                </Button>
              </div>
            </div>
          )}
        </Modal.Content>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {step === 1 && file && !uploadError && (
            <Button 
              onClick={() => setStep(2)}
              disabled={isUploading}
            >
              Continue
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Field Mapping Modal */}
      <FieldMappingModal
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        selectedEvent={selectedEvent}
        parsedData={parsedData}
        onComplete={handleMappingComplete}
      />
    </>
  );
};

export default ImportModal;
